import { Input } from '@ember/component';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { t } from 'ember-intl';

const SPECIAL_CHARACTERS = /[!@#$%^&*()_+\-=[\]{};:\\|,.<>/?]/;
const UPPER_CHARACTERS = /[A-Z]/;
const NUMBER_CHARACTERS = /\d/;

type WChangePasswordFormSignature = {
  Element: HTMLFormElement;
  Args: {
    loginActionPasswordText: 'login.setPasswordText' | 'login.resetPasswordText';
    loginActionPasswordLabel: 'login.createPasswordLabel' | 'login.newPasswordLabel';
    loginActionPasswordButton: 'login.setPasswordButton' | 'login.changePasswordButton';
  };
};

export default class WChangePasswordForm extends Component<WChangePasswordFormSignature> {
  @tracked private password?: string;
  @tracked private confirmPassword?: string;

  @tracked private checked = false;
  @action private checkClicked(): void {
    this.checked = !this.checked;
  }

  private get isCountChars(): boolean {
    return this.password !== undefined && this.password.length >= 8;
  }
  get countCharsClass(): 'check' | 'noCheck' {
    return this.isCountChars ? 'check' : 'noCheck';
  }

  private get isSpecialChars(): boolean {
    return this.password !== undefined && SPECIAL_CHARACTERS.test(this.password);
  }
  get specialCharsClass(): 'check' | 'noCheck' {
    return this.isSpecialChars ? 'check' : 'noCheck';
  }

  private get isUppercaseChars(): boolean {
    return this.password !== undefined && UPPER_CHARACTERS.test(this.password);
  }
  get uppercaseCharsClass(): 'check' | 'noCheck' {
    return this.isUppercaseChars ? 'check' : 'noCheck';
  }

  private get isNnumberChars(): boolean {
    return this.password !== undefined && NUMBER_CHARACTERS.test(this.password);
  }
  get numberCharsClass(): 'check' | 'noCheck' {
    return this.isNnumberChars ? 'check' : 'noCheck';
  }

  private get isNotMatch(): boolean {
    const { password, confirmPassword } = this;
    return !!password && !!confirmPassword && password !== confirmPassword;
  }
  private get notMatchClass(): 'error' | 'noError' {
    return this.isNotMatch ? 'error' : 'noError';
  }

  private get submitDisabled(): boolean {
    return !(
      this.isCountChars &&
      this.isSpecialChars &&
      this.isUppercaseChars &&
      this.isNnumberChars &&
      this.password === this.confirmPassword
    );
  }

  <template>
    <form class='form' method='post' ...attributes>
      <div class='explanation-line'>
        <div>{{t 'login.createPasswordText'}}</div>
        <div>{{t @loginActionPasswordText}}</div>
      </div>

      <div class='spacer-small'></div>

      <div class='inner-form'>
        <input hidden autocomplete='username' />
        <div class='line'>
          <Input
            @type={{if this.checked 'text' 'password'}}
            @value={{this.password}}
            name='password'
            autocomplete='new-password'
            placeholder={{t @loginActionPasswordLabel}}
            autofocus
          />
        </div>
        <div class='line'>
          <Input
            @type={{if this.checked 'text' 'password'}}
            @value={{this.confirmPassword}}
            name='confirmPassword'
            autocomplete='new-password'
            placeholder={{t 'login.confirmPasswordLabel'}}
          />
        </div>

        <div class='line small'>
          <div class='checkBox'>
            <div class='first-line'>
              <div
                class='tickBox {{if this.checked "checked"}}'
                role='button'
                {{on 'click' this.checkClicked}}
              ></div>
              <div class='content-area'>
                {{t 'login.passwordShowLabel'}}
              </div>
            </div>
          </div>
        </div>

        <div class='check-lines'>
          <div class='explanation'>
            {{t 'login.password.criteria'}}
          </div>
          <div class={{this.countCharsClass}}>
            {{t 'login.password.countChars'}}
          </div>
          <div class={{this.specialCharsClass}}>
            {{t 'login.password.specialChars'}}
          </div>
          <div class={{this.uppercaseCharsClass}}>
            {{t 'login.password.uppercaseChars'}}
          </div>
          <div class={{this.numberCharsClass}}>
            {{t 'login.password.numberChars'}}
          </div>
          <div class={{this.notMatchClass}}>
            {{t 'login.password.notMatch'}}
          </div>
        </div>
      </div>

      <div class='spacer'></div>

      <div class='expanded-line'>
        <div>
          {{! empty div for layout }}
        </div>
        <button type='submit' class='quick-button-right' disabled={{this.submitDisabled}}>
          {{t @loginActionPasswordButton}}
        </button>
      </div>
    </form>
  </template>
}
